import { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { isOk, readItem } from '../../../utils/apiResult';

// ═══ PANEL DEBRIEF — istemci modeli (SPEC F4) ═══════════════════════════════
//
// Uç sözleşmesi (SPEC F4, backend ajanıyla paylaşılan):
//   GET  /v1/ats-interviews/{id}/debrief  → karşılaştırma yükü
//        { interview, template{competencies[]}, evaluators[], rows[], overall,
//          recommendationCounts, debrief|null, canRecord, allSubmitted, debriefRequired }
//   POST /v1/ats-interviews/{id}/debrief  → kayıt
//   PUT  /v1/ats-interviews/{id}/debrief  → düzeltme
//   GET  /v1/ats-applications/{id}/debriefs → başvurunun debrief kayıtları (liste)
//
// ANCHORING: katılımcı kendi kartını göndermeden GET 403
// `ats.debrief.submitOwnFirst` döner. services/api.js okumalarda REJECT ETMEZ
// ve varsayılan olarak hata gövdesini atar — 403 "boş" ile karışmasın diye
// `passErrorBody` ile gövde korunur ve ANAHTARA bakılır. Bu durum bir hata
// değil, kuralın kendisidir: toast basılmaz (yalnız bu anahtar için).

export const DEBRIEF_DECISIONS = ['advance', 'hold', 'reject', 'more_info'];
export const RECOMMENDATION_ORDER = ['strong_yes', 'yes', 'neutral', 'no', 'strong_no'];
export const SUBMIT_OWN_FIRST = 'ats.debrief.submitOwnFirst';

export const DECISION_TONE = {
  advance: 'green',
  hold: 'amber',
  reject: 'red',
  more_info: 'blue',
};

// Ağırlık sunucuda kesir (toplam 1.0) ya da yüzde olarak gelebilir (eski
// şablonlar). Gösterim her zaman yüzde: ≤ 1 ise kesir kabul edilir.
export const weightPercent = (w) => {
  const n = Number(w);
  if (!Number.isFinite(n) || n <= 0) return null;
  const pct = n <= 1 ? n * 100 : n;
  return Math.round(pct * 10) / 10;
};

export const fmtScore = (v, lang) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  try {
    return new Intl.NumberFormat(lang || 'tr', { maximumFractionDigits: 2 }).format(n);
  } catch { return String(Math.round(n * 100) / 100); }
};

// Sunucunun `disagreement` bayrağı TEK KAYNAKTIR (spread ≥ ölçeğin %40'ı).
// İstemci yeniden hesaplamaz — iki ayrı kural ayrışırdı.
export const isDisagreement = (row) => row?.disagreement === true;

/** "yyyy-MM-ddTHH:mm" (tarayıcı-yerel) — DateTimePicker'ın beklediği biçim. */
export const toLocalDtString = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Tek mülakatın debrief yükü.
 * status: 'loading' | 'ok' | 'submitOwnFirst' | 'failed'
 */
export function useInterviewDebrief(interviewId) {
  const [state, setState] = useState({ status: 'loading', data: null });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!interviewId) return undefined;
    let cancelled = false;
    setState((prev) => ({ status: prev.data ? prev.status : 'loading', data: prev.data }));
    api.get(`/v1/ats-interviews/${interviewId}/debrief`, {
      passErrorBody: true,
      skipErrorToast: [SUBMIT_OWN_FIRST],
    }).then((res) => {
      if (cancelled) return;
      if (isOk(res)) {
        setState({ status: 'ok', data: res.data.data || null });
      } else if (res?.data?.message === SUBMIT_OWN_FIRST) {
        setState({ status: 'submitOwnFirst', data: null });
      } else {
        setState({ status: 'failed', data: null });
      }
    });
    return () => { cancelled = true; };
  }, [interviewId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { ...state, reload };
}

/** POST (yeni) / PUT (düzeltme). Başarıda true; hata interceptor'da toast'lanır. */
export async function saveDebrief(interviewId, body, { isEdit }) {
  try {
    const res = isEdit
      ? await api.put(`/v1/ats-interviews/${interviewId}/debrief`, body)
      : await api.post(`/v1/ats-interviews/${interviewId}/debrief`, body);
    return isOk(res);
  } catch {
    return false;
  }
}

/** Başvurunun debrief kayıtları — okunamazsa `ok:false` (boş ≠ yüklenemedi). */
export async function fetchApplicationDebriefs(applicationId) {
  // Arka plan okuması: kapsam dışı aktörde (403/404) toast BASILMAZ — bölüm
  // yalnız görünmez olur. Hata anahtarı kullanıcıya bir şey anlatmaz.
  const res = await api.get(`/v1/ats-applications/${applicationId}/debriefs`, { skipErrorToast: true });
  const d = res?.data?.data;
  const items = Array.isArray(d) ? d : (d?.data || []);
  return { ok: isOk(res), items: isOk(res) ? items : [] };
}

// Mülakatın debrief-zorunlu şablonu var mı? Sunucu satıra bayrak koyarsa
// (`debriefRequired` / `isPanelDebriefRequired`) o okunur; yoksa çapalı şablon
// (`scorecardId`) mevcut GET /v1/ats-scorecards/{id} ucundan sorulur.
const rowRequiredFlag = (iv) => {
  if (typeof iv?.debriefRequired === 'boolean') return iv.debriefRequired;
  if (typeof iv?.isPanelDebriefRequired === 'boolean') return iv.isPanelDebriefRequired;
  return undefined;
};

/**
 * Başvuru detayı için debrief durumu: mülakat başına kayıt + zorunluluk.
 * pending = tamamlanmış + debrief-zorunlu + kaydı OLMAYAN mülakatlar (kapının aynası).
 */
export function useApplicationDebriefs(applicationId, interviews) {
  const [byInterview, setByInterview] = useState({});
  const [required, setRequired] = useState({});
  const [loaded, setLoaded] = useState(false);

  const list = Array.isArray(interviews) ? interviews : [];
  const completed = list.filter((iv) => iv.status === 'completed');
  const completedKey = completed.map((iv) => `${iv.id}:${iv.scorecardId || ''}:${rowRequiredFlag(iv)}`).join('|');

  useEffect(() => {
    if (!applicationId || completed.length === 0) {
      setByInterview({}); setRequired({}); setLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setLoaded(false);

    const debriefsP = fetchApplicationDebriefs(applicationId).then(({ items }) => {
      const map = {};
      items.forEach((d) => { if (d?.interviewId) map[d.interviewId] = d; });
      return map;
    });

    const templateIds = [...new Set(completed
      .filter((iv) => rowRequiredFlag(iv) === undefined && iv.scorecardId)
      .map((iv) => iv.scorecardId))];
    const templatesP = Promise.all(templateIds.map((sid) => api.get(`/v1/ats-scorecards/${sid}`, { skipErrorToast: true })
      .then((res) => [sid, readItem(res)?.isPanelDebriefRequired === true])))
      .then((pairs) => Object.fromEntries(pairs));

    Promise.all([debriefsP, templatesP]).then(([map, tpl]) => {
      if (cancelled) return;
      const req = {};
      completed.forEach((iv) => {
        const flag = rowRequiredFlag(iv);
        req[iv.id] = flag !== undefined ? flag : (iv.scorecardId ? tpl[iv.scorecardId] === true : false);
      });
      setByInterview(map);
      setRequired(req);
      setLoaded(true);
    });
    return () => { cancelled = true; };
    // completedKey özetler: aynı mülakat kümesi için yeniden istek atılmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, completedKey]);

  const pending = completed.filter((iv) => required[iv.id] && !byInterview[iv.id]);
  return { byInterview, required, pending, loaded };
}
